import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import * as qs from 'qs';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  AverageScoreTeamMode,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  AverageLimit,
  PlayerPlayingStatus,
  SearchSortDirection,
  SeasonEligibility,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import type {
  AnalyticTag,
  Props as InstantCardSearchProps,
} from '@sorare/core/src/components/search/InstantSearch/types';
import {
  useConfigContext,
  type PrintablePlayerSortName,
} from '@sorare/core/src/contexts/config';
import {
  GQLSearchContext,
  keyFacetsByApiField,
} from '@sorare/core/src/contexts/graphql-search';
import { useGQLSearch } from '@sorare/core/src/contexts/graphql-search/useGQLSearch.ts';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useLazyQuery } from '@sorare/core/src/hooks/graphql/useLazyQuery';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { FILTERS, VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';
import { priceRarityVariables } from '@sorare/core/src/lib/scouting-center';

import { PriceCell } from '#src/components/searchPlayerTable/PriceCell/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import {
  DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY,
  useDefaultPrintablePlayersSearchClient,
} from '#src/hooks/search/useDefaultPrintablePlayersSearchClient.ts';
import {
  FIELDS,
  scopeFieldToTeamMode,
} from '#src/hooks/search/usePrintablePlayersSearchClient.ts';
import type { extractQueryGeneric } from '#src/lib/searchClient.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';
import { useTeamMode } from '#src/searchCommonPlayer/teamMode.ts';

import {
  BackendPlayerSearchLayout,
  PlayerSearchLayout,
} from './PlayerSearchLayout.tsx';
import type {
  AdvancedPlayersSearchQuery,
  AdvancedPlayersSearchQueryVariables,
} from './__generated__/index.graphql.ts';
import { BACKEND_FILTERS } from './filters.tsx';
import type { Props } from './types.ts';

const parseSort = (raw: string): PrintablePlayerSortName | null => {
  const [direction] = raw.split('-');
  if (direction in SearchSortDirection) {
    return raw as PrintablePlayerSortName;
  }
  return null;
};

export interface FullProps extends Props {
  defaultSort: PrintablePlayerSortName;
  sorts: PrintablePlayerSortName[];
  sport: Sport;
  defaultFilters?: string[];
  getOptionalFilters?: InstantCardSearchProps['getOptionalFilters'];
  analyticsTags: AnalyticTag[];
  defaultHitsPerPage?: number;
  fetchPolicy?: 'network-only' | 'cache-first';
  withStartersOn?: boolean;
  shouldSkip?: boolean;
}

const ADVANCED_PLAYERS_SEARCH_QUERY = gql`
  query AdvancedPlayersSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $seasonEligibility: SeasonEligibility!
    $withLimitedMarketValue: Boolean!
    $withRareMarketValue: Boolean!
    $withSuperRareMarketValue: Boolean!
    $withUniqueMarketValue: Boolean!
    $inSeason: Boolean
    $onlyPrimary: Boolean!
    $withLimitedFloorPrice: Boolean!
    $withRareFloorPrice: Boolean!
    $withSuperRareFloorPrice: Boolean!
    $withUniqueFloorPrice: Boolean!
    $averageLimit: AverageLimit!
    $onlyFollowed: Boolean
    $lastOwnedCardsRarities: [Rarity!]
    $teamMode: AverageScoreTeamMode
    $skipTeamSlugs: Boolean! = true
  ) {
    searchPlayers(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
      onlyFollowed: $onlyFollowed
    ) {
      ...useDefaultPrintablePlayersSearchClient_searchPrintablePlayers
      commonPlayerHits {
        id
        ...PriceCell_commonPlayer
        anyPlayer {
          slug
          ...BackendPlayerSearchLayout_AnyPlayerInterface
        }
      }
    }
  }
  ${useDefaultPrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ${BackendPlayerSearchLayout.fragments.AnyPlayerInterface}
  ${
    // oxlint-disable-next-line sorare/no-detached-fragments
    PriceCell.fragments.commonPlayer
  }
` as TypedDocumentNode<
  AdvancedPlayersSearchQuery,
  AdvancedPlayersSearchQueryVariables
>;

type Query = typeof ADVANCED_PLAYERS_SEARCH_QUERY;

const FavoriteFilterChangeNotifier = ({
  onChange,
}: {
  onChange: (v: boolean) => void;
}) => {
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name,
  });

  useEffect(() => {
    if (currentRefinement !== undefined) onChange(currentRefinement);
  }, [currentRefinement, onChange]);

  return null;
};

export const AdvancedPlayerSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  fetchPolicy,
  withStartersOn = true,
  favoritesWatchlist = false,
  shouldSkip = false,
  lastOwnedCardsRarities,
  ...rest
}: FullProps) => {
  const { statsManager } = useManagePlayerSearchView();
  const index = defaultSort || rest.sorts[0];
  const [onlyFollowed, setOnlyFollowed] = useState(favoritesWatchlist);
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();
  const searchClient = useDefaultPrintablePlayersSearchClient<Query>(
    ADVANCED_PLAYERS_SEARCH_QUERY,
    {
      onlyFollowed,
      fetchPolicy,
      skip: shouldSkip,
      variables: {
        seasonEligibility:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON
            ? SeasonEligibility.IN_SEASON
            : SeasonEligibility.CLASSIC,
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        ...priceRarityVariables(
          statsManager[PlayerSearchViewAttributes.MARKET_VALUE_RARITIES],
          statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY]
        ),
        averageLimit: AverageLimit.LAST_10,
        lastOwnedCardsRarities,
      },
    },
    (data: extractQueryGeneric<Query>) => data.searchPlayers
  );

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{
        sport,
        withMyBidsBanner: false,
      }}
    >
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <InstantCardSearch
          {...(index && { indexes: [index] })}
          availableSorts={rest.sorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          getOptionalFilters={getOptionalFilters}
          urlState
          distinct
          {...(withStartersOn && {
            initialIndexUIState: {
              refinementList: {
                [FILTERS.playingStatus.attribute]: [
                  PlayerPlayingStatus.STARTER,
                  PlayerPlayingStatus.REGULAR,
                ].map(v => v.toLowerCase()),
              },
            },
          })}
          {...(defaultHitsPerPage && { defaultHitsPerPage })}
          {...(rest.attributesToRetrieve && {
            attributesToRetrieve: rest.attributesToRetrieve,
          })}
          searchClient={searchClient}
        >
          {!favoritesWatchlist && (
            <FavoriteFilterChangeNotifier onChange={setOnlyFollowed} />
          )}
          <PlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            favoritesWatchlist={favoritesWatchlist}
            lastOwnedCardsRarities={lastOwnedCardsRarities}
            {...rest}
          >
            {children}
          </PlayerSearchLayout>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};

export const BackendAdvancedPlayerSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  fetchPolicy,
  withStartersOn = true,
  favoritesWatchlist = false,
  shouldSkip = false,
  lastOwnedCardsRarities,
  ...rest
}: FullProps) => {
  const { statsManager } = useManagePlayerSearchView();
  const index = defaultSort || rest.sorts[0];
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();
  const {
    so5: { so5Competitions },
  } = useConfigContext();

  const Wrapper = useCallback(
    ({ children: c }: { children: ReactNode }) => (
      <CurrentMarketplaceUserProvider sport={sport}>
        {c}
      </CurrentMarketplaceUserProvider>
    ),
    [sport]
  );

  const { ctxBase, facets, searchParams } = useGQLSearch(BACKEND_FILTERS);
  const { query, page, activeFacets, refinements, setSearchParams } = ctxBase;

  const teamMode = useTeamMode();

  const initialUrlParams = useMemo(
    () => ({
      ...(withStartersOn && {
        [FILTERS.playingStatus.urlAttribute]: [
          PlayerPlayingStatus.STARTER,
          PlayerPlayingStatus.REGULAR,
        ].map(v => v.toLowerCase()),
      }),
    }),
    [withStartersOn]
  );

  const hasNavigatedToInitialFilters = useRef(
    Object.keys(initialUrlParams).every(key => searchParams.has(key))
  );

  const parsedSorts = searchParams
    .get('s')
    ?.split(',')
    .map(parseSort)
    .filter(Boolean);
  const sorts = parsedSorts?.length ? parsedSorts : [index];

  const onlyFollowed = Boolean(
    favoritesWatchlist || activeFacets.ff[0] === 'true'
  );

  const { data, loading, previousData, refetch } = useQuery(
    ADVANCED_PLAYERS_SEARCH_QUERY,
    {
      // oxlint-disable-next-line react/refs
      skip: shouldSkip || !hasNavigatedToInitialFilters.current,
      variables: {
        query,
        page,
        pageSize: 20,
        advancedFilters: [
          `sport:${sport.toLowerCase()}`,
          ...(defaultFilters ?? []),
          ...(teamMode === 'NATIONAL_TEAM'
            ? ['has_active_national_team:true']
            : []),
          ...(activeFacets.lf.filter(Boolean).length
            ? [
                so5Competitions.find(e => e.slug === activeFacets.lf[0])
                  ?.algoliaFilters,
              ]
                .filter(Boolean)
                .map(e => `(${e})`)
            : []),
        ].join(' AND '),
        sorts: sorts
          .map(s => {
            const [direction, alias] = s.split('-');
            return alias in FIELDS
              ? {
                  field: scopeFieldToTeamMode(
                    FIELDS[alias as keyof typeof FIELDS],
                    teamMode
                  ),
                  direction: direction as SearchSortDirection,
                }
              : null;
          })
          .filter(Boolean),
        refinements: refinements.map(r => ({
          ...r,
          field: scopeFieldToTeamMode(r.field, teamMode),
        })),
        facets,
        seasonEligibility:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON
            ? SeasonEligibility.IN_SEASON
            : SeasonEligibility.CLASSIC,
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        ...priceRarityVariables(
          statsManager[PlayerSearchViewAttributes.MARKET_VALUE_RARITIES],
          statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY]
        ),
        onlyFollowed,
        averageLimit: AverageLimit.LAST_10,
        lastOwnedCardsRarities,
        teamMode: teamMode as AverageScoreTeamMode,
        skipTeamSlugs: teamMode === 'ALL',
      },
    }
  );

  const [search] = useLazyQuery(
    DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY,
    {
      variables: {
        query,
        advancedFilters: [
          `sport:${sport.toLowerCase()}`,
          ...(defaultFilters ?? []),
          ...(activeFacets.lf.filter(Boolean).length
            ? [
                so5Competitions.find(e => e.slug === activeFacets.lf[0])
                  ?.algoliaFilters,
              ]
            : []),
        ].join(' AND '),
        refinements,
        onlyFollowed,
        // We'll override these variables when calling search
        field: '',
        facetQuery: '',
      },
    }
  );

  const safeData = data ?? previousData;

  useEffect(() => {
    if (!parsedSorts?.length) {
      setSearchParams(prev => {
        prev.delete('s');
        return prev;
      });
    }

    if (hasNavigatedToInitialFilters.current) return;

    const initialParamKeys = Object.keys(initialUrlParams);
    const notInitialSearchParams = Object.fromEntries(
      Array.from(searchParams).filter(
        ([key]) => !initialParamKeys.includes(key)
      )
    );

    const queryString = qs.stringify(
      { ...notInitialSearchParams, ...initialUrlParams },
      {
        format: 'RFC1738',
        arrayFormat: 'comma',
        encodeValuesOnly: true,
        skipNulls: true,
        addQueryPrefix: true,
      }
    );

    hasNavigatedToInitialFilters.current = true;
    setSearchParams(new URLSearchParams(queryString), { replace: true });
  }, [parsedSorts?.length, searchParams, setSearchParams, initialUrlParams]);

  return (
    <ConditionalWrapper wrap={!currentMarketplaceUserContext} Wrapper={Wrapper}>
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <GQLSearchContext
          value={{
            ...ctxBase,
            search,
            extractFacetValues: searchFacetData =>
              searchFacetData.searchPlayerFacetValues,
            pages: safeData?.searchPlayers.nbPages ?? 0,
            nbHits: safeData?.searchPlayers.nbHits ?? 0,
            advancedFilters: '', // TODO ADVANCED_FILTERS,
            onSaleOnly: true,
            sorts,
            facets: keyFacetsByApiField([
              {
                field: 'lf',
                values: so5Competitions
                  .filter(comp => comp.scoutable)
                  .map(comp => ({
                    value: comp.slug,
                    label: comp.displayName,
                    count: 1,
                  })),
              },
              ...(safeData?.searchPlayers.facets ?? []),
            ]),
          }}
        >
          {/* {!favoritesWatchlist && (
            <FavoriteFilterChangeNotifier onChange={setOnlyFollowed} />
          )} */}
          <BackendPlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            favoritesWatchlist={favoritesWatchlist}
            lastOwnedCardsRarities={lastOwnedCardsRarities}
            items={safeData?.searchPlayers.commonPlayerHits ?? []}
            loading={loading}
            refetch={refetch}
            {...rest}
          >
            {children}
          </BackendPlayerSearchLayout>
        </GQLSearchContext>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
